import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Instructor, instructorsProvider } from "../providers/instructorsProvider";
import { Student } from "../providers/studentsProvider";
import { useMemo, useState } from "react";
import { useRefresh } from "react-admin";
import { Bundle, catalogProvider } from "../providers/catalogProvider";
import { reportError } from "../backoffice.utils";
import { autovioColors } from "../misc/backofficeTheme";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { DialogProps } from "../misc/DialogProps";
import { PricesOverview } from "../misc/PricesOverview";
import { BundleAutocomplete } from "../inputs/BundleAutocomplete";
import { DrivingLicenseClass } from "../model/DrivingLicenseClass";

type AddBundleDialogStep = "choose bundle" | "prices overview" | "choose instructor" | "adding bundle";

export function AddBundleDialog({ student, ...dialogProps }: DialogProps & { student: Student }) {
  const closeDialog = dialogProps.onClose;
  const refresh = useRefresh();
  const [bundle, setBundle] = useState(undefined as Bundle | undefined);
  const [selectedDrivingLicenseClasses, setSelectedDrivingLicenseClasses] = useState<DrivingLicenseClass[]>([]);
  const [instructors, setInstructors] = useState(undefined as Array<Instructor> | undefined);
  const [step, setStep] = useState<AddBundleDialogStep>("choose bundle");
  const bookedDrivingLicenseClasses = useMemo(
    () => new Set(student.bookedTrainings.map((training) => training.drivingLicenseClass)),
    [student.bookedTrainings],
  );
  const reset = () => {
    setBundle(undefined);
    setSelectedDrivingLicenseClasses([]);
    setInstructors(undefined);
    setStep("choose bundle");
  };
  const handleClose = () => {
    reset();
    closeDialog();
  };
  const addBundle = async (bundle: Bundle, instructorId: string) => {
    setStep("adding bundle");
    await catalogProvider.addBundle({ studentId: student.id, bundleId: bundle.id, instructorId });
    showInfoToast(`Paket ${bundle.name} hinzugefügt.`);
    refresh();
    reset();
    closeDialog();
  };
  const confirmBundle = async () => {
    if (!bundle) return;
    setStep("adding bundle");
    try {
      const validInstructors = await determineValidInstructors(student, bundle);
      const validInstructorIds = validInstructors.map((it) => it.id);
      // Prefer an instructor the student already knows ...
      for (const instructorId of student.instructorIds) {
        if (validInstructorIds.includes(instructorId)) {
          await addBundle(bundle, instructorId);
          return;
        }
      }
      // No instructor the student already knows can teach the selected bundle ...
      if (validInstructors.length === 1) {
        await addBundle(bundle, validInstructors[0].id);
      } else {
        // Let the backoffice user choose an instructor ...
        setInstructors(validInstructors);
        setStep("choose instructor");
      }
    } catch (error) {
      reportError("Failed to add bundle", error);
      showErrorToast("Oje, das hat leider nicht geklappt.");
      setStep("prices overview");
    }
  };
  const onInstructorSelected = async (selectedInstructor: Instructor) => {
    try {
      await addBundle(bundle!, selectedInstructor.id);
    } catch (error) {
      reportError("Failed to add bundle", error);
      showErrorToast("Oje, das hat leider nicht geklappt.");
      setStep("choose instructor");
    }
  };
  return (
    <Dialog {...dialogProps} onClose={handleClose} maxWidth={step === "choose bundle" ? "sm" : "md"} fullWidth>
      <DialogTitle>
        {step === "choose bundle" && "Paket auswählen"}
        {step === "prices overview" && bundle?.name}
        {step === "choose instructor" && `Fahrlehrer für Paket ${bundle?.name} auswählen`}
        {step === "adding bundle" && `Paket ${bundle?.name} hinzufügen`}
      </DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <DialogContent>
        {step === "choose bundle" && (
          <BundleAutocomplete
            drivingSchoolId={student.drivingSchoolId}
            drivingLicenseClasses={selectedDrivingLicenseClasses}
            onDrivingLicenseClassesChange={setSelectedDrivingLicenseClasses}
            value={bundle}
            onChange={setBundle}
            filterBundle={(candidate) =>
              candidate.trainings.length > 0 &&
              !candidate.trainings.some((training) => bookedDrivingLicenseClasses.has(training.drivingLicenseClass))
            }
          />
        )}
        {step === "prices overview" && bundle && (
          <>
            <DialogContentText sx={{ mb: "1em", fontStyle: "italic" }}>
              Hinweis: Wenn du ein Paket hinzufügst, erhält der Fahrschüler eine Benachrichtigung in der App mit der
              Bitte den zusätzlichen Grundbetrag zu zahlen. Es ist keine manuelle Rechnung nötig.
            </DialogContentText>
            <PricesOverview newBundle={bundle} />
          </>
        )}
        {step === "choose instructor" && instructors && (
          <_ChooseInstructorList instructors={instructors} onSelect={onInstructorSelected} />
        )}
        {step === "adding bundle" && <LinearProgress />}
      </DialogContent>
      {step === "choose bundle" && (
        <DialogActions>
          <Button
            variant="outlined"
            endIcon={<NavigateNextIcon />}
            disabled={!bundle}
            onClick={() => setStep("prices overview")}
          >
            Weiter zur Preisübersicht
          </Button>
        </DialogActions>
      )}
      {step === "prices overview" && (
        <DialogActions>
          <Button startIcon={<NavigateBeforeIcon />} variant="outlined" onClick={() => setStep("choose bundle")}>
            Zurück
          </Button>
          <Button variant="contained" onClick={confirmBundle}>
            Paket hinzufügen
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

function _ChooseInstructorList({
  instructors,
  onSelect,
}: {
  instructors: Array<Instructor>;
  onSelect: (instructor: Instructor) => void;
}) {
  return (
    <List>
      {instructors.map((instructor) => (
        <ListItemButton key={instructor.id} onClick={() => onSelect(instructor)}>
          <ListItemAvatar>
            <Avatar src={instructor.avatarUrl} />
          </ListItemAvatar>
          <ListItemText primary={<span style={{ color: autovioColors.green }}>{instructor.name}</span>} />
        </ListItemButton>
      ))}
    </List>
  );
}

async function determineValidInstructors(student: Student, bundle: Bundle): Promise<Array<Instructor>> {
  const instructorsOfDrivingSchool = await instructorsProvider.getByDrivingSchoolId(bundle.drivingSchoolId);
  const bundleDrivingLicenseClasses = bundle.trainings.map((training) => training.drivingLicenseClass);
  const validInstructors = instructorsOfDrivingSchool.filter((instructor) => {
    return bundleDrivingLicenseClasses.every((it) => instructor.instructing.drivingLicenseClasses.includes(it));
  });
  return validInstructors;
}
